import { useState } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Campaign, CampaignWinner, getError, useToast } from '@aslanpay/portal-common-lib';

import useCampaignDecision from 'app/hooks/useCampaignDecision';
import useFetchRecognitionValues from 'app/hooks/useFetchRecognitionValues';

const MAX_MESSAGE = 2000;

interface RewordWinnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pride: Campaign;
  winner: CampaignWinner;
  companyId: string;
}

/** Rewording a decision before it publishes. Who won is not on offer here — that was final on confirmation. */
function RewordWinnerModal({ isOpen, onClose, pride, winner, companyId }: RewordWinnerModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [valueId, setValueId] = useState(winner.valueId ?? '');
  const [message, setMessage] = useState(winner.winnerMessage ?? '');

  const { data: values } = useFetchRecognitionValues({ companyId, isEnabled: true }, isOpen);

  const { mutate: reword, isLoading } = useCampaignDecision.useUpdateWinner({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Decision reworded',
        description: 'The announcement will publish with the new words.',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
      onClose();
    },
    onError: (error) =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not reword the decision',
        description: getError(error).message || 'Nothing was saved. Please try again.',
      }),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="reword-winner-modal">
        <ModalHeader>
          Reword {`${winner.firstName} ${winner.lastName}`.trim()}&apos;s win
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="16px">
            <FormControl>
              <FormLabel>{pride.valuesRequired ? 'Value' : 'Value (optional)'}</FormLabel>
              <Select
                placeholder="Choose a value"
                value={valueId}
                onChange={(event) => setValueId(event.target.value)}
                data-testid="reword-value-select"
              >
                {(values?.list ?? []).map((value) => (
                  <option key={value.id} value={value.id}>
                    {value.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Published with the win</FormLabel>
              <Textarea
                value={message}
                maxLength={MAX_MESSAGE}
                onChange={(event) => setMessage(event.target.value)}
                data-testid="reword-message-input"
              />
              <FormHelperText>
                Everyone in the company reads this. {MAX_MESSAGE - message.length} characters left.
              </FormHelperText>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={onClose} data-testid="cancel-reword">
            Cancel
          </Button>
          <Button
            onClick={() =>
              reword({
                campaignId: pride.id,
                request: { valueId: valueId || undefined, winnerMessage: message.trim() },
              })
            }
            isDisabled={isLoading || message.trim().length === 0}
            isLoading={isLoading}
            data-testid="confirm-reword"
          >
            Save wording
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RewordWinnerModal;
